import { Affix, Button, Col, Collapse, Divider, Row, message } from 'antd';
import React, { useMemo, useState } from 'react';
import './SupplierCard.scss';
import Markdown from 'react-markdown';
import { SupplierRiskData } from 'models/SupplierDynamics';
import { SupplierTabType } from '../../sb/shared/constants';
import { FeedbackContent } from './FeedbackContent';
import remarkGfm from 'remark-gfm';
import { EditOutlined, ExportOutlined } from '@ant-design/icons';
import { useRenderedHtml } from 'hooks';
import { useUtilsApi } from 'api/useUtilsApi';
import fileDownload from 'js-file-download';
import { SupplierTitle } from '../../models/Supplier';
import { ACLCheck } from 'shared/Helpers';

const { Panel } = Collapse;

interface SupplierRiskContentProps {
  riskReport: SupplierRiskData[];
  handleToggleOpen?: () => void;
  activeKey?: number[];
  id: number;
  supplier?: SupplierTitle | undefined;
  onEditClick?: (content: string, title?: string) => void;
}

export const SupplierRiskContent: React.FC<SupplierRiskContentProps> = ({
  riskReport,
  handleToggleOpen,
  activeKey,
  id,
  supplier,
  onEditClick
}): React.ReactElement => {
  const { ref, html } = useRenderedHtml<HTMLDivElement>();
  const { htmlToPdf } = useUtilsApi();
  const [isExporting, setIsExporting] = useState(false);
  const reportType = 'Supplier Risk Report';

  // const handlePdfClick = async (): Promise<void> => {
  //   if (!html) {
  //     message.warning('No content available to export');
  //     return;
  //   }

  //   if (!supplier?.name) {
  //     message.warning('Supplier name is required for PDF export');
  //     return;
  //   }

  //   setIsExporting(true);
  //   try {
  //     const pdfBlob = await htmlToPdf(html, supplier.name, reportType);
  //     const filename = `NPI Research-${supplier.name}-${reportType}.pdf`;
  //     fileDownload(pdfBlob, filename);
  //   } catch (error) {
  //     console.error('Failed to generate PDF:', error);
  //     message.error('Failed to generate PDF. Please try again.');
  //   } finally {
  //     setIsExporting(false);
  //   }
  // };

  const handlePdfClick = async (): Promise<void> => {
    if (!html) {
      message.warning('No content available to export');
      return;
    }

    if (!supplier?.name) {
      message.warning('Supplier name is required for PDF export');
      return;
    }

    setIsExporting(true);

    try {
      // 👇 Inject PDF-safe styles directly into HTML
      const pdfHtml = `
      <style>
        /* ===== PDF HEADER ROW FIX ===== */
    
        .ant-row {
          display: block !important;
          width: 100% !important;
          white-space: nowrap !important;
          margin-bottom: 16px !important; /* 🔼 MORE vertical space between sections */
        }
    
        .ant-col {
          display: inline-block !important;
          vertical-align: middle !important;
        }
    
        /* ===== NUMBER BOX COLORS ===== */
    
        /* Positive */
        .ant-col.bg-green-500 {
          background-color: #22c55e !important;
          color: #ffffff !important;
        }
    
        /* Neutral */
        .ant-col.bg-orange-400 {
          background-color: #fb923c !important;
          color: #ffffff !important;
        }
    
        /* Negative (if exists) */
        .ant-col.bg-red-500 {
          background-color: #ef4444 !important;
          color: #ffffff !important;
        }
    
        /* Common number box styling */
        .ant-col.bg-green-500,
        .ant-col.bg-orange-400,
        .ant-col.bg-red-500 {
          width: 42px !important;
          height: 42px !important;
          line-height: 42px !important;
          text-align: center !important;
          font-weight: 700 !important;
          font-size: 18px !important;
          border-radius: 6px !important;
          margin-right: 12px !important;
        }
    
        /* Positive / Neutral / Negative text */
        .ant-col span {
          font-size: 18px !important;
          font-weight: 600 !important;
          margin-right: 12px !important;
          display: inline-block !important;
        }
    
        /* Pipe divider */
        .ant-divider-vertical {
          display: inline-block !important;
          height: 24px !important;
          margin: 0 12px !important;
          border-left: 2px solid #666 !important;
          position: relative !important;
          top: 2px !important;
        }
    
        /* Title */
        .ant-col.text-2xl {
          font-size: 18px !important;
          font-weight: 600 !important;
          display: inline-block !important;
        }
    
        /* ===== CONTENT SPACING ===== */
    
        .supplier-risk {
          display: block !important;
          margin-top: 12px !important;   /* 🔼 more space after header */
          margin-bottom: 20px !important; /* 🔼 more space before next section */
          white-space: normal !important;
        }
    
        .supplier-risk p {
          margin: 10px 0 !important;     /* 🔼 more vertical breathing room */
          line-height: 1.6 !important;
        }
    
        .supplier-risk ul,
        .supplier-risk ol {
          margin: 10px 0 10px 20px !important;
        }
    
        .supplier-risk li {
          margin: 6px 0 !important;
        }
      </style>
    
      ${html}
    `;

      const pdfBlob = await htmlToPdf(pdfHtml, supplier.name, reportType);
      const filename = `NPI Supplier Risk-${supplier.name}-${reportType}.pdf`;
      fileDownload(pdfBlob, filename);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      message.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const combinedRiskContent = useMemo(() => {
    if (!riskReport || riskReport.length === 0) {
      return '';
    }
    const sanitizeSentiment = (text: string): string =>
      text
        .replace(/Sentiment Score:\s*\d+/gi, '')
        .replace(/Sentiment Score:\s*(Positive|Neutral|Negative)/gi, '')
        .replace(/Sentiment Score:/gi, '')
        .trim();

    return riskReport
      .map((report) => {
        const heading = report.title ? `## ${report.title}` : '';
        const cleanedPayload = sanitizeSentiment(report.payload || '');
        return [heading, cleanedPayload].filter(Boolean).join('\n\n');
      })
      .join('\n\n');
  }, [riskReport]);

  return (
    <div className="flex">
      <div className="disclaimer-collapse w-11/12">
        {ACLCheck(['update:supplier_risk_report']) && (
          <div className="flex justify-end mb-4">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              className="!bg-[#0070FF] !border-[#0070FF] !px-4 !py-2 !h-auto"
              onClick={() => onEditClick?.(combinedRiskContent, 'Edit Supplier Risk')}
            >
              Edit Information
            </Button>
          </div>
        )}
        <div ref={ref}>
          {riskReport && riskReport.length > 0 ? (
            riskReport.map((report, index) => (
              <Row key={index} className="items-center">
                <Col
                  className={`text-white text-2xl rounded-md mr-2 h-12 w-12 flex items-center justify-center ${
                    report.score === 1
                      ? 'bg-green-500'
                      : report.score === 0 || report.score === null
                        ? 'bg-orange-400'
                        : 'bg-orange-400'
                  }`}
                >
                  {report.score !== null ? report.score : '0'}
                </Col>
                <Col>
                  {report.score === 1 ? (
                    <span className="ml-2 text-2xl font-bold">Positive</span>
                  ) : report.score === 0 || report.score === null ? (
                    <span className="ml-2 text-2xl font-bold">Neutral</span>
                  ) : null}
                </Col>
                <Divider type="vertical" style={{ height: '3rem', borderColor: '#656565' }} className="mx-4" />
                <Col className="text-2xl font-semibold">{report.title}</Col>
                <div className="supplier-risk mt-4">
                  <Markdown remarkPlugins={[remarkGfm]}>{report.payload}</Markdown>
                </div>
              </Row>
            ))
          ) : (
            <>
              <p>No risk report data available.</p>
            </>
          )}
        </div>
        <div className="mt-8 border-t border-gray-200 pt-2">
          <Collapse
            expandIconPosition="end"
            expandIcon={() =>
              activeKey?.length === 0 ? (
                <div className="!text-[11px] font-normal text-gray-500">Show disclaimer</div>
              ) : (
                <div className="!text-[11px] font-normal text-gray-500">Hide disclaimer</div>
              )
            }
            onChange={handleToggleOpen}
            activeKey={activeKey}
            ghost
          >
            <Panel
              header={<div className="text-xs font-semibold text-gray-500">Supplier Risk Disclaimer</div>}
              key={1}
              showArrow={true}
            >
              <div className="-mt-1 text-xs text-gray-500">
                This report was generated using artificial intelligence, public information, and NPI’s proprietary data.
                AI can make mistakes; consider checking important information.
              </div>
            </Panel>
          </Collapse>
        </div>
      </div>
      <Affix offsetTop={250}>
        <div className="flex flex-col gap-4 items-center text-center">
          <Button
            type="link"
            icon={<ExportOutlined />}
            onClick={handlePdfClick}
            loading={isExporting}
            className="!text-[#0F62FE] !px-0 !h-auto flex items-center gap-1 font-medium"
          >
            Export to PDF
          </Button>
          <div className="w-full flex justify-center">
            <FeedbackContent id={id} supplierTabType={SupplierTabType.REPORT} />
          </div>
        </div>
      </Affix>
    </div>
  );
};
